import React, { useEffect, useRef, useState } from 'react'
import { Link, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

import { useResendCodeMutation } from '../../services'
import { useSnackbarContext } from '../index.ts'

const ONE_SECOND_INTERVAL = 1000
const CODE_LIFESPAN_MINS = 5
export interface ResendCodeProps {
  login: string
  apiUrl: string
  codeLifespanMin?: number
}

export const ResendCode = ({
  login,
  apiUrl,
  codeLifespanMin = CODE_LIFESPAN_MINS,
}: ResendCodeProps): React.ReactElement => {
  const { addSnack } = useSnackbarContext()

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const expTimeRef = useRef<Dayjs>(dayjs().add(codeLifespanMin, 'minutes'))
  const [secLeft, setSecLeft] = useState(dayjs(expTimeRef.current).diff(dayjs(), 'seconds'))
  const { mutate, isError, isSuccess, isPending } = useResendCodeMutation(login, apiUrl)

  const startTimer = () => {
    //Учитываем что первый коллбэк интервал выполнит лишь через промежуток времени из-за этого может
    // возникнуть ситуация когда скачком изменятся 'Запрашиваем код...' : 'Отправить код заново'
    // делаем уставновку времени до интервала чтобы убрать скачок
    const diff = Math.max(0, expTimeRef.current.diff(dayjs(), 'seconds'))
    setSecLeft(diff)

    if (diff <= 0) {
      resetTimer()
      return
    }

    timerRef.current = setInterval(() => {
      const diff = Math.max(0, expTimeRef.current.diff(dayjs(), 'seconds'))
      setSecLeft(diff)

      if (diff <= 0) {
        resetTimer()
      }
    }, ONE_SECOND_INTERVAL)
  }

  const resetTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
  }

  useEffect(() => {
    startTimer()

    return () => {
      resetTimer()
    }
  }, [])

  useEffect(() => {
    if (isSuccess) {
      expTimeRef.current = dayjs().add(codeLifespanMin, 'minutes')
      resetTimer()
      startTimer()
    }
  }, [isSuccess])

  useEffect(() => {
    if (isError) {
      addSnack({
        message: 'Не удалось запросить новый код, попробуйте ещё раз',
        severity: 'error',
      })
    }
  }, [isError])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  if (secLeft <= 0) {
    return (
      <Link
        component="button"
        form="codeResend"
        onClick={() => mutate()}
        underline={isPending ? 'none' : 'always'}
        variant="body1"
        disabled={isPending}
      >
        {isPending ? 'Запрашиваем код...' : 'Отправить код заново'}
      </Link>
    )
  }

  return (
    <Typography color="text.secondary" mt={1}>
      Запросить код можно через {formatTime(secLeft)}
    </Typography>
  )
}
